import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import FuseUtils from "../../../utils/FuseUtils";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import ColorPicker from "../../../components/ColorPicker";
import Combobox from "../../../components/common/Combobox";
import EvidenceTempleateItem from "../../evidenceTemplate/components/EvidenceTemplateItem";
import NotificationItem from "../../../components/common/NotificationItem";
import { MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION } from "../../../common/utils";
import NotificationSetup from "../../../components/common/NotificationSetup";
import ExpandableView from "../../../components/common/ExpandableView";

export default function EventTypeInfo() {
  const {
    eventTypeModel,
    setEventTypeModel,
    config,
    DYNAMIC_FORM_ENDPOINT,
    WORKFLOW_ENDPOINT,
    OPERATIONAL_AREA_API,
  } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [dynamicFormData, setDynamicFormData] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [operationalAreaData, setOperationalAreaData] = useState(null);

  const extractAdditionalInfo = (jsonData) => {
    if (jsonData && jsonData.length > 0) {
      const data = JSON.parse(jsonData);

      return data;
    }

    return [];
  };

  const [additionalData, setAdditionalData] = useState(
    extractAdditionalInfo(eventTypeModel.AdditionalFieldsJson)
  );
  const [newAdditionalDataName, setNewAdditionalDataName] = useState("");

  const getDynamicFormData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${DYNAMIC_FORM_ENDPOINT}/`, requestConfig);

    setDynamicFormData(data?.data);
  };

  const getWorkflowData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WORKFLOW_ENDPOINT}/`, requestConfig);

    setWorkflowData(data?.data);
  };

  const getOperationalAreaData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${OPERATIONAL_AREA_API}/`, requestConfig);

    setOperationalAreaData(data?.data);
  };

  useEffect(() => {
    getDynamicFormData();
    getWorkflowData();
    getOperationalAreaData();
  }, []);

  const handleReviewFormChange = (value) => {
    if (!value) {
      setEventTypeModel({
        ...eventTypeModel,
        ReviewFormId: 0,
        ReviewForm: null,
      });
      return;
    }

    setEventTypeModel({
      ...eventTypeModel,
      ReviewFormId: value.Id,
      ReviewForm: value,
    });
  };

  const handleOperationalAreaChange = (value) => {
    if (!value) {
      setEventTypeModel({
        ...eventTypeModel,
        OperationalAreaId: 0,
        operationalArea: null,
      });
      return;
    }

    setEventTypeModel({
      ...eventTypeModel,
      OperationalAreaId: value.Id,
      operationalArea: value,
      BackgroundColor: value.BackgroundColor,
      ForegroundColor: value.ForegroundColor,
    });
  };

  const handleWorkflowChange = (value) => {
    if (!value) {
      setEventTypeModel({
        ...eventTypeModel,
        WorkFlowId: 0,
        Workflow: null,
      });
      return;
    }

    setEventTypeModel({
      ...eventTypeModel,
      WorkFlowId: value.Id,
      Workflow: value,
    });
  };

  const handleChange = (e) => {
    if (e.target.name === "Description") {
      setDescriptionCount(e.target.value.length);
    }

    if (
      [
        "AttentionDays",
        "SemaphoreRed",
        "SemaphoreYellow",
        "SemaphoreRed",
      ].includes(e.target.name) &&
      e.target.value < 0
    ) {
      setEventTypeModel({ ...eventTypeModel, [e.target.name]: 0 });
      return;
    }

    setEventTypeModel({ ...eventTypeModel, [e.target.name]: e.target.value });
  };

  const handleNewAdditionalFieldNameChange = (e) => {
    setNewAdditionalDataName(e.target.value);
  };

  const handleAddBtn = () => {
    additionalData.push({
      guid: FuseUtils.generateGUID(),
      name: newAdditionalDataName,
      description: "",
    });

    setNewAdditionalDataName("");
  };

  const handleDeleteButton = (guid) => {
    const index = additionalData.findIndex((x) => x.guid === guid);
    if (index >= 0) {
      additionalData.splice(index, 1);
      setAdditionalData([...additionalData]);
    }
  };

  useEffect(() => {
    setEventTypeModel({
      ...eventTypeModel,
      AdditionalFields: additionalData,
    });
  }, [additionalData]);

  const handleCheckedChanged = (e) => {
    setEventTypeModel({
      ...eventTypeModel,
      [e.target.name]: e.target.checked,
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <form className="w-full flex flex-col gap-2 p-2">
                <div className="flex flex-wrap my-2 w-full">
                  <div className="w-full">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      name="Name"
                      label={localize.name}
                      variant="outlined"
                      onChange={handleChange}
                      value={eventTypeModel.Name}
                    />
                  </div>
                </div>

                <div className="w-full flex gap-2 my-2">
                  <FormControl required fullWidth>
                    <Combobox
                      isRequired
                      id="operationalAreaId"
                      fieldLabel={localize.operational_area}
                      fieldOptions={operationalAreaData?.Data ?? []}
                      fieldValue={eventTypeModel.operationalArea}
                      handleValueChange={handleOperationalAreaChange}
                    />
                  </FormControl>
                </div>

                <div className="w-full flex gap-2 my-2">
                  <FormControl required fullWidth>
                    <Combobox
                      isRequired
                      id="WorkFlowId"
                      fieldLabel={localize.workflow}
                      fieldOptions={workflowData?.Data ?? []}
                      fieldValue={eventTypeModel?.Workflow ?? null}
                      handleValueChange={handleWorkflowChange}
                    />
                  </FormControl>
                </div>

                <div className="my-2" style={{ marginBottom: "10px" }}>
                  <FormControl required fullWidth>
                    <Combobox
                      id="ReviewFormId"
                      fieldLabel={localize.visit_report}
                      fieldOptions={dynamicFormData?.Data ?? []}
                      fieldValue={eventTypeModel?.ReviewForm ?? null}
                      handleValueChange={handleReviewFormChange}
                    />
                  </FormControl>
                </div>

                <div className="w-full flex gap-2 my-2">
                  <div style={{ width: "50%" }}>
                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={eventTypeModel.executionValue}
                      onChange={handleChange}
                      size="small"
                      id="executionValue"
                      name="executionValue"
                      label={localize.service_value}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <div style={{ width: "50%" }}>
                    <FormControlLabel
                      size="small"
                      name="RequestSignature"
                      control={
                        <Checkbox
                          size="small"
                          name="RequestSignature"
                          checked={eventTypeModel.RequestSignature}
                          onChange={handleCheckedChanged}
                        />
                      }
                      label={
                        <span style={{ fontSize: "0.8em" }}>
                          {localize.request_signature}
                        </span>
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-wrap my-2 w-full">
                  <div className="w-full">
                    <TextField
                      InputLabelProps={getInputLabelProps()}
                      inputProps={{
                        ...getInputProps(),
                        maxLength: maximumCharactersForDescription,
                      }}
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      id="outlined-basic"
                      name="Description"
                      label={localize.description}
                      variant="outlined"
                      onChange={handleChange}
                      value={eventTypeModel.Description}
                      helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                    />
                  </div>
                </div>

                <Box className="flex flex-col gap-2 items-center">
                  <Alert severity="info" className="w-full">
                    <p className="text-xs">
                      {localize.color_explanation.replace(
                        "%1s",
                        localize.color_explanation_complement_event_type
                      )}
                    </p>
                  </Alert>
                  <Box className="w-full flex justify-between gap-2 mt-2">
                    <Box>
                      <ColorPicker
                        id="bg-color-picker"
                        value={eventTypeModel.BackgroundColor}
                        name="BackgroundColor"
                        onChange={handleChange}
                        labelText={localize.background_color}
                      />
                    </Box>

                    <Box>
                      <ColorPicker
                        id="fg-color-picker"
                        value={eventTypeModel.ForegroundColor}
                        name="ForegroundColor"
                        onChange={handleChange}
                        labelText={localize.foreground_color}
                      />
                    </Box>
                  </Box>
                </Box>
              </form>
            ),
          },
          {
            Name: localize.attention,
            Component: (
              <Box className="mt-4 flex flex-col gap-2">
                <Box className="flex gap-2 w-full items-center">
                  <div style={{ width: "50%" }}>
                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={eventTypeModel.AttentionDays}
                      onChange={handleChange}
                      size="small"
                      id="AttentionDays"
                      name="AttentionDays"
                      label={localize.attention_days}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <Alert severity="info" className="w-full">
                    <p className="text-xs">{localize.info_attention_days}</p>
                  </Alert>
                </Box>

                <Box className="flex gap-2 w-full items-center">
                  <div style={{ width: "50%" }}>
                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={eventTypeModel.SemaphoreRed}
                      onChange={handleChange}
                      size="small"
                      id="SemaphoreRed"
                      name="SemaphoreRed"
                      label={localize.days_quantity}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <Alert severity="error" className="w-full">
                    <p className="text-xs">{localize.event_red_semaphore}</p>
                  </Alert>
                </Box>

                <Box className="flex gap-2 w-full items-center">
                  <div style={{ width: "50%" }}>
                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={eventTypeModel.SemaphoreYellow}
                      onChange={handleChange}
                      size="small"
                      id="SemaphoreYellow"
                      name="SemaphoreYellow"
                      label={localize.days_quantity}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <Alert severity="warning" className="w-full">
                    <p className="text-xs">{localize.event_yellow_semaphore}</p>
                  </Alert>
                </Box>
              </Box>
            ),
          },
          {
            Name: localize.additional_data,
            Component: (
              <div className="flex flex-col my-4 w-full p-2">
                <div
                  className="flex items-center w-full"
                  style={{ gap: "10px", marginBottom: "10px" }}
                >
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={{
                      ...getInputProps(),
                    }}
                    size="small"
                    fullWidth
                    id="outlined-basic"
                    name="new_additional_field_name"
                    label={localize.enter_new_field_name}
                    variant="outlined"
                    onChange={handleNewAdditionalFieldNameChange}
                    value={newAdditionalDataName}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    className="app_general_button_data app_success_button btn-sm"
                    onClick={handleAddBtn}
                    disabled={
                      !newAdditionalDataName ||
                      newAdditionalDataName.length === 0
                    }
                    style={{ width: "130px" }}
                  >
                    <label className="mr-2 report_little_text">
                      {localize.add}
                    </label>
                    <Add className="text-lg" />
                  </Button>
                </div>

                <Box className="w-full flex flex-col gap-2">
                  {additionalData && additionalData.length > 0 ? (
                    additionalData.map((field) => (
                      <EvidenceTempleateItem
                        key={field.guid}
                        evidence={{
                          Name: field.name,
                        }}
                        onDeleteEvidence={() => {
                          handleDeleteButton(field.guid);
                        }}
                      />
                    ))
                  ) : (
                    <NotificationItem
                      notification={{
                        type: 0,
                        message: localize.no_data,
                      }}
                    />
                  )}
                </Box>
              </div>
            ),
          },
          {
            Name: localize.notification,
            Component: (
              <NotificationSetup
                entity={eventTypeModel}
                setEntity={setEventTypeModel}
                subtitle={localize.notify_event_type}
                showOnlyDefinedToUser
              />
            ),
          },
        ]}
      />
    </div>
  );
}
